import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Chip,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add as AddIcon, 
  Search as SearchIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Medication as MedicationIcon,
  Category as CategoryIcon,
  LocalPharmacy as PharmacyIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { medicineAPI } from '../services/api';
import StatCard from '../components/StatCard';

const Medicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    total: 0,
    drops: 0,
    tablets: 0,
    ointments: 0
  });
  
  const [medicineForm, setMedicineForm] = useState({
    name: '',
    genericName: '',
    type: 'drops',
    strength: '',
    manufacturer: '',
    description: '',
  });

  useEffect(() => {
    fetchMedicines();
  }, [search]);

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      const response = await medicineAPI.getMedicines({ search, limit: 100 });
      const medicinesData = response.data.data;
      setMedicines(medicinesData);
      
      // Calculate stats
      const total = medicinesData.length;
      const drops = medicinesData.filter(m => m.type === 'drops').length;
      const tablets = medicinesData.filter(m => m.type === 'tablet').length;
      const ointments = medicinesData.filter(m => m.type === 'ointment').length;
      
      setStats({ total, drops, tablets, ointments });
    } catch (error) {
      showSnackbar('Failed to fetch medicines', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (medicine = null) => {
    if (medicine) {
      setEditingMedicine(medicine);
      setMedicineForm({
        name: medicine.name,
        genericName: medicine.genericName || '',
        type: medicine.type,
        strength: medicine.strength || '',
        manufacturer: medicine.manufacturer || '',
        description: medicine.description || '',
      });
    } else {
      setEditingMedicine(null);
      setMedicineForm({
        name: '',
        genericName: '',
        type: 'drops',
        strength: '',
        manufacturer: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleSaveMedicine = async () => {
    try {
      if (editingMedicine) {
        await medicineAPI.updateMedicine(editingMedicine._id, medicineForm);
        showSnackbar('Medicine updated successfully!');
      } else {
        await medicineAPI.createMedicine(medicineForm);
        showSnackbar('Medicine created successfully!');
      }
      setOpenDialog(false);
      fetchMedicines();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save medicine', 'error');
    }
  };

  const handleDeleteMedicine = async (id) => {
    if (window.confirm('Are you sure you want to delete this medicine?')) {
      try {
        await medicineAPI.deleteMedicine(id);
        showSnackbar('Medicine deleted successfully!');
        fetchMedicines();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Failed to delete medicine', 'error');
      }
    }
  };

  const columns = [
    { field: 'name', headerName: 'Medicine Name', width: 200 },
    { field: 'genericName', headerName: 'Generic Name', width: 180 },
    { field: 'type', headerName: 'Type', width: 120 },
    { field: 'strength', headerName: 'Strength', width: 100 },
    { field: 'manufacturer', headerName: 'Manufacturer', width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton color="primary" onClick={() => handleOpenDialog(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteMedicine(params.row._id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Filter medicines by type
  const filteredMedicines = typeFilter === 'all' 
    ? medicines 
    : medicines.filter(m => m.type === typeFilter);

  return (
    <Box sx={{ pt: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          Medicines
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <StatCard label="Total Medicines" value={stats.total} icon={<MedicationIcon fontSize="small" />} color="primary" />
        <StatCard label="Eye Drops" value={stats.drops} icon={<PharmacyIcon fontSize="small" />} color="info" />
        <StatCard label="Tablets" value={stats.tablets} icon={<CategoryIcon fontSize="small" />} color="secondary" />
        <StatCard label="Ointments" value={stats.ointments} icon={<TrendingUpIcon fontSize="small" />} color="success" />
      </Box>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Medicine
        </Button>
      </Box>

      {/* Search and Filter Bar */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 'none' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search medicines by name, generic name, or manufacturer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Filter by Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="tablet">Tablets</MenuItem>
              <MenuItem value="capsule">Capsules</MenuItem>
              <MenuItem value="syrup">Syrups</MenuItem>
              <MenuItem value="drops">Drops</MenuItem>
              <MenuItem value="ointment">Ointments</MenuItem>
              <MenuItem value="injection">Injections</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper variant="outlined" sx={{ height: 600, width: '100%', borderRadius: 2, boxShadow: 'none' }}>
        <DataGrid
          rows={filteredMedicines}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(124, 132, 212, 0.05)',
            },
          }}
        />
      </Paper>

      {/* Add/Edit Medicine Dialog - Enhanced */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMedicine ? 'Edit Medicine' : 'Add New Medicine'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medicine Name"
                required
                value={medicineForm.name}
                onChange={(e) => setMedicineForm({ ...medicineForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Generic Name"
                value={medicineForm.genericName}
                onChange={(e) => setMedicineForm({ ...medicineForm, genericName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Type"
                required
                value={medicineForm.type}
                onChange={(e) => setMedicineForm({ ...medicineForm, type: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="tablet">Tablet</option>
                <option value="capsule">Capsule</option>
                <option value="syrup">Syrup</option>
                <option value="drops">Drops</option>
                <option value="ointment">Ointment</option>
                <option value="injection">Injection</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Strength"
                placeholder="e.g., 0.5%"
                value={medicineForm.strength}
                onChange={(e) => setMedicineForm({ ...medicineForm, strength: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={medicineForm.manufacturer}
                onChange={(e) => setMedicineForm({ ...medicineForm, manufacturer: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={medicineForm.description}
                onChange={(e) => setMedicineForm({ ...medicineForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveMedicine}
            variant="contained"
          >
            {editingMedicine ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Medicines;
